// src/components/StarQuestRoadmap.js
import React, { useMemo } from "react";

/**
 * Roadmap grid with a progress roadway and chapter stops.
 * Props:
 * - stops: [{ title, sub?, image }]
 * - currentIndex: number (0-based) – the next stop to tackle (highlighted)
 */
export default function StarQuestRoadmap({ stops = [], currentIndex = 0 }) {
  const total = stops.length;
  const pct = useMemo(() => {
    // progress is how many are completed (strictly less than current)
    if (!total) return 0;
    const completed = Math.max(0, Math.min(total, currentIndex));
    return Math.round((completed / total) * 100);
  }, [total, currentIndex]);

  return (
    <div className="w-full">
      {/* Roadway / progress */}
      <div className="mb-5 md:mb-6">
        <div className="relative h-3 md:h-3.5 rounded-full bg-amber-200/70 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400"
            style={{ width: `${pct}%` }}
          />
          {/* subtle dashed overlay to feel like a 'road' */}
          <div className="absolute inset-0 pointer-events-none bg-[repeating-linear-gradient(90deg,transparent_0_12px,rgba(255,255,255,0.35)_12px,rgba(255,255,255,0.35)_16px,transparent_16px_28px)]" />
        </div>
        <div className="mt-1.5 text-[11px] md:text-xs text-amber-800/80">{pct}% complete</div>
      </div>

      {/* Stops grid */}
      <div
        className="
          grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6
          gap-3 sm:gap-4 md:gap-5
        "
      >
        {stops.map((s, i) => {
          const isNext = i === currentIndex;
          const isPast = i < currentIndex;
          const isLocked = i > currentIndex;

          return (
            <div
              key={`${s.title}-${i}`}
              className={[
                "group relative rounded-2xl border bg-white shadow-sm",
                "transition-transform duration-200",
                isNext
                  ? "border-amber-400 ring-2 ring-amber-300 shadow-md scale-[1.01]"
                  : "border-amber-200 hover:shadow-md hover:scale-[1.005]",
                isLocked ? "opacity-95" : "",
              ].join(" ")}
              title={isPast ? "Star collected" : isNext ? "Next stop" : "Locked"}
              aria-label={`${i + 2}. ${s.title}${s.sub ? ` – ${s.sub}` : ""}`}
            >
              {/* Image area */}
              <div className="p-2.5 sm:p-3">
                <div
                  className="
                    relative rounded-xl bg-amber-50
                    overflow-hidden aspect-[4/3] flex items-center justify-center
                  "
                >
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      // graceful fallback: keep a soft background if image fails
                      e.currentTarget.style.display = "none";
                    }}
                  />

                  {/* Status badge (top-right) */}
                  <div
                    className={[
                      "absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2",
                      "w-6 h-6 sm:w-7 sm:h-7 rounded-full",
                      "flex items-center justify-center text-xs sm:text-sm font-bold",
                      isPast
                        ? "bg-amber-500 text-white"
                        : isNext
                        ? "bg-amber-200 text-amber-800"
                        : "bg-slate-200 text-slate-600",
                    ].join(" ")}
                  >
                    {isPast ? "★" : isNext ? "☆" : "•"}
                  </div>

                  {/* Glow for next stop */}
                  {isNext && (
                    <div className="absolute inset-0 pointer-events-none rounded-xl ring-4 ring-amber-300/50 animate-pulse" />
                  )}
                </div>
              </div>

              {/* Text area */}
              <div className="px-3 sm:px-3.5 pb-3 sm:pb-3.5">
                <div className="text-[13px] sm:text-sm font-semibold text-amber-900 line-clamp-1">
                  {i + 2}. {s.title}
                </div>
                {s.sub && (
                  <div className="text-[12px] sm:text-[13px] text-amber-700/90 line-clamp-1">
                    {s.sub}
                  </div>
                )}

                {/* mini legend chip on mobile when locked */}
                {isLocked && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-[10px]">
                    Locked
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-[13px] text-amber-800/90">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-full bg-amber-500 inline-block" /> Collected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-full bg-amber-200 inline-block" /> Next stop
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-full bg-slate-200 inline-block" /> Locked
        </span>
      </div>
    </div>
  );
}
